import { apiClient } from "./client";

export interface TycoonState {
  exchange: {
    userId: string;
    level: number;
    totalBranches: number;
    totalStaff: number;
    lastPassiveClaimAt: string;
    /** Effective rate after manager + security multipliers — what the player actually earns. */
    hourlyTokenRate: string;
    /** Pre-security ceiling. UI shows "X% of capacity" using this. */
    hourlyCapacity: string;
    /** Raw sum of staff hourly rates with no multipliers. */
    rawHourlyRate: string;
    accruedTokens: string;
    /** Hard per-claim cap. UI should clamp the local ticker to this. */
    passiveClaimCap: string;
    /** Live brand awareness = stored + accrued since last claim. */
    brandAwareness: string;
    brandAwarenessStored: string;
    brandAwarenessRate: string;
  };
  security: {
    supply: number;
    demand: number;
    /** supply - demand, may be negative. */
    net: number;
    /** clamp(net, 0, 100) — multiplier % applied to earnings. */
    effectivePct: number;
  };
  branches: Array<{
    id: string;
    city: string;
    level: number;
    openedAt: string;
    /** Sum of manager bonus pcts in this branch (0.12 = +12%). */
    managerBonusPct: number;
  }>;
  staff: Array<{
    id: string;
    type: string;
    level: number;
    branchId: string;
    hourlyTokenRate: string;
  }>;
  /** Per-branch hire cap by role. Display as "n/max" + disable hire at cap. */
  staffCaps: Record<string, number>;
  /** Full city catalog. Server-driven so the FE doesn't have to mirror the
   * 37-entry city table; renders the picker directly off this list. */
  cities: Array<{
    code: string;
    name: string;
    region: "CAUCASUS" | "EUROPE" | "ASIA" | "AMERICAS" | "MIDDLE_EAST";
    flag: string;
    unlockBranchCount: number;
    openCostTokens: string;
    openCostBrandAwareness: string;
    isOwned: boolean;
    isUnlocked: boolean;
  }>;
}

export async function fetchTycoonState(): Promise<TycoonState> {
  const { data } = await apiClient.get<TycoonState>("/tycoon/state");
  return data;
}

export async function openBranch(city: string): Promise<{ branchId: string }> {
  const { data } = await apiClient.post<{ branchId: string }>("/tycoon/branch", { city });
  return data;
}

export type StaffType =
  | "CASHIER"
  | "TRADER"
  | "QUANT"
  | "MANAGER"
  | "MARKETING"
  | "SECURITY";

export async function hireStaff(input: { branchId: string; type: string }): Promise<{ staffId: string }> {
  const { data } = await apiClient.post<{ staffId: string }>("/tycoon/staff", input);
  return data;
}

export async function upgradeStaff(input: { staffId: string; targetLevel: number }): Promise<{ newLevel: number; newRate: string }> {
  const { data } = await apiClient.post<{ newLevel: number; newRate: string }>(
    "/tycoon/staff/upgrade",
    input
  );
  return data;
}

export async function claimPassive(): Promise<{ amountClaimed: string; hoursAccrued: number }> {
  const { data } = await apiClient.post<{ amountClaimed: string; hoursAccrued: number }>(
    "/tycoon/passive/claim"
  );
  return data;
}
